import { Outlet, useLocation } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import { SaleInvoicesPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FeatureAvailabilityType } from 'modules/core/hooks/useFeatureAvailability'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { SaleInvoicesListTable } from 'modules/finance/components/Invoices/SaleInvoicesListTable'
import { useArchiveSaleInvoiceDialog } from 'modules/finance/hooks/sale_invoices/useArchiveSaleInvoiceDialog'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const SaleInvoicesPage = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { access, company, employee: currentEmployee } = useCurrentSession()
  const location = useLocation()
  const locale = access.locale
  const { showArchiveSaleInvoiceDialog } = useArchiveSaleInvoiceDialog()

  const {
    data,
    loading: loadingQuery,
    error,
  } = useQuery(SaleInvoicesPageDocument, {
    variables: {
      companyId: company.id,
      featureIds: [Features.sales_invoices_creation],
    },
  })

  const canManageSalesInvoices =
    data?.finance?._policies?.financialDocumentsManageSales?.can ?? false

  const createInvoicesFeatureAvailable =
    data?.finance?._policies?.financialDocumentsCreateSale?.can ?? false

  const legalEntitiesOptions =
    data?.companies.legalEntitiesConnection.nodes.map((legalEntity) => ({
      label: legalEntity.legalName || '',
      value: legalEntity.id,
    })) || []

  const featureUsage: FeatureAvailabilityType = data?.companies.featureAvailabilitiesConnection
    .nodes[0] ?? {
    available: false,
    id: Features.sales_invoices_creation,
    _internalType: 'feature',
  }

  const availableCurrencies = data
    ? data.apiCore.currenciesConnection.edges.map((edge) => edge.node)
    : []

  useEffect(() => {
    track(trackingEvents.accounting.saleInvoices.viewSaleInvoicesPage)
  }, [])
  const usage = featureUsage.usage || 0
  const usageLimit = featureUsage.usageLimit || -1

  const isDetailPage = location.pathname.includes('/detail/')

  return (
    <ResponsivePage.Body
      variant={isDetailPage ? 'fullscreen' : 'full'}
      error={error}
      loading={loadingQuery}
      authorized={canManageSalesInvoices}
    >
      {!isDetailPage && (
        <SaleInvoicesListTable
          companyId={company.id}
          locale={locale}
          legalEntitiesOptions={legalEntitiesOptions}
          availableCurrencies={availableCurrencies}
          showDeleteModalForInvoiceId={(invoiceId) => showArchiveSaleInvoiceDialog({ invoiceId })}
          onClickAdd={() => {
            navigate(navigation.finance.sales.salesInvoices.create.fullPath())
          }}
          detailPath={(invoiceId) =>
            navigation.finance.sales.salesInvoices.invoiceId.fullPath({ invoiceId })
          }
          onClickExport={() => {
            navigate(navigation.finance.sales.salesInvoices.exporter.fullPath())
          }}
          createInvoicesFeatureAvailable={createInvoicesFeatureAvailable}
          usage={usage}
          usageLimit={usageLimit}
          currentEmployeeId={currentEmployee?.id || -1}
          pageDetailPath={(invoiceId) =>
            navigation.finance.sales.salesInvoices.detail.fullPath({ invoiceId })
          }
        />
      )}
      <Outlet />
    </ResponsivePage.Body>
  )
}

export default SaleInvoicesPage
